import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import ScrollToTop from './components/common/ScrollToTop';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import ProtectedRoute from './routes/ProtectedRoute';
import PublicRedirect from './routes/PublicRedirect';
import InstallPWA from './components/common/InstallPWA';
import OpenInExternalBrowser from './components/common/OpenInExternalBrowser';
import AppTabBar from './components/common/AppTabBar';
import PushForeground from './components/common/PushForeground';
import { currentStorefrontHost, currentCustomDomain, ROOT_DOMAIN } from './utils/subdomain';
import { API_BASE_URL } from './config';
// Aliased: this file already has an `Analytics` — the creator-facing
// analytics PAGE, lazy-loaded below. Two bindings of the same name in one
// module is a SyntaxError, and the two things could hardly be less alike.
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';
import { pageview } from './utils/ga';
import { applyConsent } from './utils/consent';
import ConsentBanner from './components/common/ConsentBanner';
import useReferral from './hooks/useReferral';
import { PlatformProvider, usePlatform } from './hooks/usePlatform';
import { ChromeProvider, useChromeState } from './hooks/useChrome';

// ─── MARKETING & AUTH ───────────────────────────────────────────────────────
const HomePage         = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/HomePage'));
const GetStarted       = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/GetStarted'));
const Login            = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/Login'));
const FAQ              = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/FAQ'));
const HowItWorks       = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/HowItWorks'));
// Own chunk: the guides carry all their prose with them, and none of it is
// needed by anyone who is not reading a guide.
const GuidesIndex      = lazy(() => import(/* webpackChunkName: "guides" */ './pages/GuidesIndex'));
const GuidePage        = lazy(() => import(/* webpackChunkName: "guides" */ './pages/GuidePage'));
const Contact          = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/Contact'));
const Payments         = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/Payments'));
const Terms            = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/Terms'));
const Press            = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/Press'));
const PrivacyPolicy    = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/PrivacyPolicy'));
const DataDeletion     = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/DataDeletion'));
const MintlinkPlus      = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/MintlinkPlus'));
const ReviewMyWorkLanding = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/ReviewMyWorkLanding'));
const ServicesLanding     = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/ServicesLanding'));
const InstagramAutoDMLanding = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/InstagramAutoDMLanding'));
const UpgradeToForms      = lazy(() => import(/* webpackChunkName: "marketing" */ './pages/forms/UpgradeToForms'));
// Cinematic rebuild — its own chunk so GSAP + ScrollTrigger never land in the
// marketing bundle every other landing page shares.
const TicketsLanding    = lazy(() => import(/* webpackChunkName: "tickets-landing" */ './pages/tickets-landing'));
// Own chunk: the nine comparison pages carry all their prose and every
// competitor's quoted pricing with them, and none of it belongs in the
// bundle a creator downloads to reach their dashboard.
const ComparePage       = lazy(() => import(/* webpackChunkName: "compare" */ './pages/ComparePage'));

// ─── PUBLIC CREATOR PAGES — buyer-critical, smallest possible chunk ──────────
const PublicPage       = lazy(() => import(/* webpackChunkName: "public-page" */ './pages/PublicPage'));

// ─── DISCOVERY ───────────────────────────────────────────────────────────────
const CreatorMarketplace  = lazy(() =>
  import(/* webpackChunkName: "discover" */ './pages/CreatorMarketplace')
    .then(m => ({ default: m.CreatorMarketplace }))
);
const PublicCreatorProfile = lazy(() => import(/* webpackChunkName: "discover" */ './pages/PublicCreatorProfile'));
const ProductMarketplace   = lazy(() => import(/* webpackChunkName: "discover" */ './pages/ProductMarketplace'));
const ReviewServicePage    = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/ReviewServicePage'));
const GigPage              = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/GigPage'));
const PublicForm           = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/forms/PublicForm'));

// ─── CHECKOUT & PAYMENTS ─────────────────────────────────────────────────────
const Checkout         = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/Checkout'));
const PhysicalCheckout = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/PhysicalCheckout'));
const SupportPage = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/SupportPage'));
const PaymentSuccess   = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/PaymentSuccess'));
const PaymentFailure   = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/PaymentFailure'));
const Download         = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/Download'));
const RatePurchasePage = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/RatePurchasePage'));
const FormDownload     = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/forms/FormDownload'));
const UploadViaLink    = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/UploadViaLink'));

// ─── BUYER ───────────────────────────────────────────────────────────────────
const BuyerLogin       = lazy(() => import(/* webpackChunkName: "buyer" */ './pages/BuyerLogin'));
const BuyerDashboard   = lazy(() => import(/* webpackChunkName: "buyer" */ './pages/BuyerDashboard'));
const ReferralLanding  = lazy(() => import(/* webpackChunkName: "buyer" */ './pages/ReferralLanding'));
const ReviewOrderBuyerView = lazy(() => import(/* webpackChunkName: "buyer" */ './pages/ReviewOrderBuyerView'));
const GigOrderBuyerView    = lazy(() => import(/* webpackChunkName: "buyer" */ './pages/GigOrderBuyerView'));

// ─── BLOG ────────────────────────────────────────────────────────────────────
const BlogListPage     = lazy(() => import(/* webpackChunkName: "blog" */ './pages/BlogListPage'));
const BlogPostPage     = lazy(() => import(/* webpackChunkName: "blog" */ './pages/BlogPostPage'));

// ─── CREATOR DASHBOARD ───────────────────────────────────────────────────────
const CreatorDashboard = lazy(() => import(/* webpackChunkName: "creator" */ './pages/CreatorDashboard'));
const CreatorProfile   = lazy(() => import(/* webpackChunkName: "creator" */ './pages/CreatorProfilePage'));
const BankDetails      = lazy(() => import(/* webpackChunkName: "creator" */ './pages/BankDetails'));
const Analytics        = lazy(() => import(/* webpackChunkName: "creator" */ './pages/Analytics'));
const Transactions     = lazy(() => import(/* webpackChunkName: "creator" */ './pages/Transactions'));
const ListingReports   = lazy(() => import(/* webpackChunkName: "creator" */ './pages/ListingReports'));
const Payouts          = lazy(() => import(/* webpackChunkName: "creator" */ './pages/Payouts'));
const Orders           = lazy(() => import(/* webpackChunkName: "creator" */ './pages/Orders'));
const BusinessVerification = lazy(() => import(/* webpackChunkName: "creator" */ './pages/BusinessVerification'));
const OrderTracking    = lazy(() => import(/* webpackChunkName: "buyer" */ './pages/OrderTracking'));
const Supporters = lazy(() => import(/* webpackChunkName: "creator" */ './pages/Supporters'));
const Referrals        = lazy(() => import(/* webpackChunkName: "creator" */ './pages/Referrals'));
const Support          = lazy(() => import(/* webpackChunkName: "creator" */ './pages/Support'));
const NewTicket        = lazy(() => import(/* webpackChunkName: "creator" */ './pages/NewTicket'));
const TicketDetails    = lazy(() => import(/* webpackChunkName: "creator" */ './pages/TicketDetails'));
const MyPages          = lazy(() => import(/* webpackChunkName: "creator" */ './pages/MyPages'));
const InstagramAutoDM  = lazy(() => import(/* webpackChunkName: "creator" */ './pages/InstagramAutoDM'));
const CreatorReviews   = lazy(() => import(/* webpackChunkName: "creator" */ './pages/CreatorReviews'));
const DiscountCodes    = lazy(() => import(/* webpackChunkName: "creator" */ './pages/DiscountCodes'));
const ConnectedApps    = lazy(() => import(/* webpackChunkName: "creator" */ './pages/ConnectedApps'));
// Own chunk: the page an AI app sends a creator to. It handles sign-in itself
// (and comes back after), so it is not wrapped in ProtectedRoute.
const ConnectAuthorize = lazy(() => import(/* webpackChunkName: "connect" */ './pages/ConnectAuthorize'));
const DigitalProductsList = lazy(() => import(/* webpackChunkName: "creator" */ './pages/DigitalProductsList'));
// Its own chunk rather than "creator": the forum carries its own text renderer,
// avatar generator and comment tree, and none of it is needed by the dashboard
// — which every creator loads on every visit, whether or not they ever open
// the community.
const Community        = lazy(() => import(/* webpackChunkName: "community" */ './pages/Community'));
const CommunityThread  = lazy(() => import(/* webpackChunkName: "community" */ './pages/CommunityThread'));
const CommunityJoin    = lazy(() => import(/* webpackChunkName: "community" */ './pages/CommunityJoin'));
const CommunityMe      = lazy(() => import(/* webpackChunkName: "community" */ './pages/CommunityMe'));
// Its own chunk: three.js and the effect composer are ~600kB that no other
// route needs, and folding them into "creator" would slow every dashboard load.
const PosterStudio     = lazy(() => import(/* webpackChunkName: "poster-studio" */ './pages/PosterStudio'));
// MintLink Lab. Standalone visual experiments that live outside the product's
// chrome — own chunk so nothing in the app pays for them.
const ReceiptLab       = lazy(() => import(/* webpackChunkName: "lab" */ './pages/ReceiptLab'));
const LabIndex         = lazy(() => import(/* webpackChunkName: "lab" */ './pages/LabIndex'));
// Its own chunk, not "lab": it pulls in the real Checkout/PaymentSuccess/
// Download components, so it shares the "checkout" chunk rather than doubling it.
const BuyerJourneyLab  = lazy(() => import(/* webpackChunkName: "lab-journey" */ './pages/BuyerJourneyLab'));
const PaletteLab       = lazy(() => import(/* webpackChunkName: "lab-palette" */ './pages/PaletteLab'));
const PosterComposerLab = lazy(() => import(/* webpackChunkName: "lab-composer" */ './pages/PosterComposerLab'));
const CarouselComposerLab = lazy(() => import(/* webpackChunkName: "lab-carousel" */ './pages/CarouselComposerLab'));
const ComponentFactoryLab = lazy(() => import(/* webpackChunkName: "lab-components" */ './pages/ComponentFactoryLab'));
const MintlinkDeckLab = lazy(() => import(/* webpackChunkName: "lab-mintlink-deck" */ './pages/MintlinkDeckLab'));
const PdfFormFillerLab = lazy(() => import(/* webpackChunkName: "lab-pdf-filler" */ './pages/PdfFormFillerLab'));
const SiteGeneratorLab = lazy(() => import(/* webpackChunkName: "lab-site-generator" */ './pages/SiteGeneratorLab'));
// Shares the storefront-builder chunk rather than doubling it: this page is the
// real renderer, the real templates and the real effects engine.
const StorefrontShowroom = lazy(() => import(/* webpackChunkName: "storefront-builder" */ './pages/StorefrontShowroom'));
const ReviewServicesList  = lazy(() => import(/* webpackChunkName: "creator" */ './pages/ReviewServicesList'));
const ReviewServiceEditor = lazy(() => import(/* webpackChunkName: "creator" */ './pages/ReviewServiceEditor'));
const FormsList   = lazy(() => import(/* webpackChunkName: "creator" */ './pages/forms/FormsList'));
const FormsEditor = lazy(() => import(/* webpackChunkName: "creator" */ './pages/forms/FormsEditor'));
const ReviewOrders        = lazy(() => import(/* webpackChunkName: "creator" */ './pages/ReviewOrders'));
const ReviewOrderDetail   = lazy(() => import(/* webpackChunkName: "creator" */ './pages/ReviewOrderDetail'));
const GigsList            = lazy(() => import(/* webpackChunkName: "creator" */ './pages/GigsList'));
const GigEditor           = lazy(() => import(/* webpackChunkName: "creator" */ './pages/GigEditor'));
const GigOrders           = lazy(() => import(/* webpackChunkName: "creator" */ './pages/GigOrders'));
const GigOrderDetail      = lazy(() => import(/* webpackChunkName: "creator" */ './pages/GigOrderDetail'));

// ─── PAGE BUILDER — separate chunk (heavy DnD libraries) ─────────────────────
const PageBuilder      = lazy(() => import(/* webpackChunkName: "page-builder" */ './pages/PageBuilder'));

// ─── MINTLINK PLUS STOREFRONT (/o/:username) ─────────────────────────────────
// Its own chunk: the section library, motion helpers and template set are only
// needed by these two routes, and the public one is the performance-sensitive
// half — a visitor landing on a creator's storefront should not download the
// builder's editing UI.
const PublicStorefront   = lazy(() => import(/* webpackChunkName: "storefront-public" */ './pages/PublicStorefront'));
const StorefrontBuilder  = lazy(() => import(/* webpackChunkName: "storefront-builder" */ './pages/StorefrontBuilder'));
const StorefrontLeads    = lazy(() => import(/* webpackChunkName: "storefront-builder" */ './pages/StorefrontLeads'));
const StorefrontDomain   = lazy(() => import(/* webpackChunkName: "storefront-builder" */ './pages/StorefrontDomain'));
// The /website hub and its two screens. Same chunk as the builder: a creator
// who opens the hub is one tap from the editor, and splitting them would buy a
// spinner on the most likely next click.
const WebsiteHub         = lazy(() => import(/* webpackChunkName: "storefront-builder" */ './pages/WebsiteHub'));
const WebsiteTemplates   = lazy(() => import(/* webpackChunkName: "storefront-builder" */ './pages/WebsiteTemplates'));
const WebsiteSeo         = lazy(() => import(/* webpackChunkName: "storefront-builder" */ './pages/WebsiteSeo'));

// ─── EVENTS / BOOKINGS ───────────────────────────────────────────────────────
const EventsList       = lazy(() => import(/* webpackChunkName: "events" */ './pages/EventsList'));
const EventEditor      = lazy(() => import(/* webpackChunkName: "events" */ './pages/EventEditor'));
const EventAttendees   = lazy(() => import(/* webpackChunkName: "events" */ './pages/EventAttendees'));
const EventCapacity    = lazy(() => import(/* webpackChunkName: "events" */ './pages/EventCapacity'));
const PublicEvent      = lazy(() => import(/* webpackChunkName: "events-public" */ './pages/PublicEvent'));
const EventTicket      = lazy(() => import(/* webpackChunkName: "events-public" */ './pages/EventTicket'));

// ─── MINTLINK QUIZ ───────────────────────────────────────────────────────────
// Split creator/public like events: a student opening a shared quiz link must
// not download the teacher's editor to sit a test.
const QuizzesList      = lazy(() => import(/* webpackChunkName: "quiz" */ './pages/quiz/QuizzesList'));
const QuizNew          = lazy(() => import(/* webpackChunkName: "quiz" */ './pages/quiz/QuizNew'));
const QuizEditor       = lazy(() => import(/* webpackChunkName: "quiz" */ './pages/quiz/QuizEditor'));
const QuizResults      = lazy(() => import(/* webpackChunkName: "quiz" */ './pages/quiz/QuizResults'));
const PublicQuiz       = lazy(() => import(/* webpackChunkName: "quiz-public" */ './pages/quiz/PublicQuiz'));
const QuizAttempt      = lazy(() => import(/* webpackChunkName: "quiz-public" */ './pages/quiz/QuizAttempt'));
const QuizBundlesList  = lazy(() => import(/* webpackChunkName: "quiz" */ './pages/quiz/QuizBundlesList'));
const QuizBundleEditor = lazy(() => import(/* webpackChunkName: "quiz" */ './pages/quiz/QuizBundleEditor'));
// Courses & Content — creator editor, plus the course page / checkout /
// library that live on the creator's website (see SubdomainSite below too).
const CoursesList    = lazy(() => import(/* webpackChunkName: "courses" */ './pages/courses/CoursesList'));
const CourseEditor   = lazy(() => import(/* webpackChunkName: "courses" */ './pages/courses/CourseEditor'));
const CoursePage     = lazy(() => import(/* webpackChunkName: "courses-site" */ './pages/courses/CoursePage'));
const CourseCheckout = lazy(() => import(/* webpackChunkName: "courses-site" */ './pages/courses/CourseCheckout'));
const Library        = lazy(() => import(/* webpackChunkName: "courses-site" */ './pages/courses/Library'));
const LibraryCourse  = lazy(() => import(/* webpackChunkName: "courses-site" */ './pages/courses/LibraryCourse'));
const PublicQuizBundle = lazy(() => import(/* webpackChunkName: "quiz-public" */ './pages/quiz/PublicQuizBundle'));
const BundlePass       = lazy(() => import(/* webpackChunkName: "quiz-public" */ './pages/quiz/BundlePass'));

// ─── MEMBERSHIPS (fan subscriptions) ─────────────────────────────────────────
// Split creator/public the same way events is: the member card and join page
// are buyer-critical and must not pull the whole editor down with them.
const MembershipsList   = lazy(() => import(/* webpackChunkName: "memberships" */ './pages/MembershipsList'));
const MembershipEditor  = lazy(() => import(/* webpackChunkName: "memberships" */ './pages/MembershipEditor'));
const MembershipMembers = lazy(() => import(/* webpackChunkName: "memberships" */ './pages/MembershipMembers'));
const MembershipCheckIn = lazy(() => import(/* webpackChunkName: "memberships" */ './pages/MembershipCheckIn'));
const MembershipAnalytics = lazy(() => import(/* webpackChunkName: "memberships" */ './pages/MembershipAnalytics'));
const MembershipStaff   = lazy(() => import(/* webpackChunkName: "memberships" */ './pages/MembershipStaff'));
// The desk-linking page is opened by somebody with no account, on a counter
// tablet, so it sits in the public chunk rather than behind the creator app.
const DeskLink          = lazy(() => import(/* webpackChunkName: "memberships-public" */ './pages/DeskLink'));
const JoinMembership    = lazy(() => import(/* webpackChunkName: "memberships-public" */ './pages/JoinMembership'));
const MemberCard        = lazy(() => import(/* webpackChunkName: "memberships-public" */ './pages/MemberCard'));

// ─── MINTLINK PLUS (subscription + checkout customizer) ──────────────────────
const PlusUpgrade        = lazy(() => import(/* webpackChunkName: "plus" */ './pages/plus/PlusUpgrade'));
const PlusSuccess        = lazy(() => import(/* webpackChunkName: "plus" */ './pages/plus/PlusSuccess'));
const PlusBilling        = lazy(() => import(/* webpackChunkName: "plus" */ './pages/plus/PlusBilling'));
const CheckoutCustomizer = lazy(() => import(/* webpackChunkName: "plus" */ './pages/plus/CheckoutCustomizer'));

// ─── ADMIN ───────────────────────────────────────────────────────────────────
const AdminLayout             = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminLayout'));
const AdminDashboard          = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminDashboard'));
const AdminCreatorsList       = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminCreatorsList'));
const AdminCreatorDetails     = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminCreatorDetails'));
const AdminAssetsList         = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminAssetsList'));
const AdminEvents             = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminEvents'));
const AdminMemberships        = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminMemberships'));
const AdminPdfForms           = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminPdfForms'));
const AdminQuizzes            = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminQuizzes'));
const AdminEventDetail        = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminEventDetail'));
const AdminSales              = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminSales'));
const AdminDiscountCodes      = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminDiscountCodes'));
const AdminPayouts            = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminPayouts'));
const AdminTaxInvoices        = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminTaxInvoices'));
const AdminReports            = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminReports'));
const AdminVisitorGeography   = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminVisitorGeography'));
const AdminGoogleDorks        = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminGoogleDorks'));
const AdminSettings           = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminSettings'));
const AdminAuditLog           = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminAuditLog'));
const AdminBankVerification   = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminBankVerification'));
const AdminApiTrail           = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminApiTrail'));
const AdminCreatorErrors      = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminCreatorErrors'));
const AdminEmailLogs          = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminEmailLogs'));
const AdminUploadLogs         = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminUploadLogs'));
const AdminListingReports     = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminListingReports'));
const AdminReviewReports      = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminReviewReports'));
const AdminReviews            = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminReviews'));
const AdminCreatorUploadHelp  = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminCreatorUploadHelp'));
const AdminSupportConsole     = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminSupportConsole'));
const AdminNotifications      = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminNotification'));
const AdminBlogManagement     = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminBlogManagement'));
const AdminReferrals          = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminReferrals'));
const AdminCommunity          = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminCommunity'));
const AdminPlanner            = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminPlanner'));
const AdminPush               = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminPush'));
const AdminPushDeliveries     = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminPushDeliveries'));
const AdminReferralDetail     = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminReferralDetail'));
const AdminSupporters         = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminSupporters'));
const AdminAnnouncements      = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminAnnouncements'));
const AdminApiMetrics       = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminApiMetrics'));
const AdminSupporterDetail    = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminSupporterDetail'));
const AdminPagesList          = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminPagesList'));
const AdminWebsites           = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminWebsites'));
const AdminFunnel             = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminFunnel'));
const AdminMarketplace        = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminMarketplace'));
const AdminPlusSubscribers    = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminPlusSubscribers'));
const AdminInstagramDM        = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminInstagramDM'));
const AdminSocialCalendar = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminSocialCalendar'));
const AdminRoles              = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminRoles'));
const AdminLocalLLMs          = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminLocalLLMs'));
const AdminAgentMetrics       = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminAgentMetrics'));
const AdminBusinessVerifications      = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminBusinessVerifications'));
const AdminBusinessVerificationDetail = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminBusinessVerificationDetail'));
const AdminPhysicalOrders            = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminPhysicalOrders'));
const AdminPhysicalCategories         = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminPhysicalCategories'));
const AdminDigitalCategories          = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminDigitalCategories'));
const AdminReviewOrders       = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminReviewOrders'));
const AdminUploadScans        = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminUploadScans'));
const AdminReviewOrderDetail  = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminReviewOrderDetail'));
const AdminReviewServicesList = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminReviewServicesList'));
const AdminReviewServiceDetail = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminReviewServiceDetail'));
const AdminGigOrders          = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminGigOrders'));
const AdminGigOrderDetail     = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminGigOrderDetail'));
const AdminGigsList           = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminGigsList'));
const AdminGigDetail          = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminGigDetail'));

// Marketing/public pages set their own <title> via <Helmet> (see e.g.
// pages/Payments.jsx, the pricing page). Nothing in the authenticated creator
// app ever did that, so switching tabs there (Dashboard → Payments → …) left
// the browser tab showing whatever title was last set — reported as "the tab
// title doesn't change". /admin/* is excluded here; AdminLayout titles itself
// from its own NAV_GROUPS so the two lists can't drift apart. Ordered
// most-specific first — the first regex to match wins.
const APP_ROUTE_TITLES = [
  [/^\/dashboard\/instagram/, 'Instagram Auto DM'],
  [/^\/dashboard/, 'Dashboard'],
  [/^\/payouts/, 'Payments'],
  [/^\/support\/new/, 'New Ticket'],
  [/^\/support\/tickets/, 'Ticket'],
  [/^\/support/, 'Support'],
  [/^\/studio\/poster/, 'Poster Studio'],
  [/^\/website\/templates/, 'Website Templates'],
  [/^\/website\/seo/, 'Website SEO'],
  [/^\/website/, 'Website'],
  [/^\/storefront\/leads/, 'Storefront Leads'],
  [/^\/storefront\/domain/, 'Storefront Domain'],
  [/^\/storefront/, 'Storefront'],
  [/^\/profile/, 'Creator Profile'],
  [/^\/bank-details/, 'Bank Details'],
  [/^\/analytics/, 'Analytics'],
  [/^\/transactions/, 'Transactions'],
  [/^\/orders/, 'Orders'],
  [/^\/business-verification/, 'Business Verification'],
  [/^\/physical-commerce/, 'Physical Commerce'],
  [/^\/supporters/, 'Supporters'],
  [/^\/events\/new/, 'New Event'],
  [/^\/events\/[^/]+\/attendees/, 'Event Attendees'],
  [/^\/events\/[^/]+\/capacity/, 'Event Capacity'],
  [/^\/events\/[^/]+\/edit/, 'Edit Event'],
  [/^\/events/, 'Events'],
  [/^\/memberships\/new/, 'New Membership Plan'],
  [/^\/memberships\/check-in/, 'Membership Check-In'],
  [/^\/memberships\/retention/, 'Membership Retention'],
  [/^\/memberships\/staff/, 'Membership Staff'],
  [/^\/memberships\/[^/]+\/members/, 'Membership Members'],
  [/^\/memberships\/[^/]+\/edit/, 'Edit Membership Plan'],
  [/^\/memberships/, 'Memberships'],
  [/^\/referrals/, 'Referrals'],
  [/^\/reviews/, 'Reviews'],
  [/^\/discount-codes/, 'Discount Codes'],
  [/^\/connected-apps/, 'Connected Apps'],
  [/^\/digital-products/, 'Digital Products'],
  [/^\/community\/join/, 'Join Community'],
  [/^\/community\/me/, 'My Community Profile'],
  [/^\/community/, 'Community'],
  [/^\/review-services\/new/, 'New Review Service'],
  [/^\/review-services/, 'Review Services'],
  [/^\/forms\/new/, 'New Form'],
  [/^\/forms/, 'Forms'],
  [/^\/quiz-bundles\/new/, 'New Quiz Bundle'],
  [/^\/quiz-bundles/, 'Quiz Bundles'],
  [/^\/quizzes\/new/, 'New Quiz'],
  [/^\/quizzes\/[^/]+\/results/, 'Quiz Results'],
  [/^\/quizzes/, 'Quizzes'],
  [/^\/review-orders/, 'Review Orders'],
  [/^\/services\/new/, 'New Service'],
  [/^\/services/, 'Services'],
  [/^\/service-orders/, 'Service Orders'],
  [/^\/plus\/success/, 'Welcome to MintLink Plus'],
  [/^\/plus\/billing/, 'MintLink Plus Billing'],
  [/^\/plus\/customize-physical/, 'Customize Checkout'],
  [/^\/plus\/customize/, 'Customize Checkout'],
  [/^\/plus/, 'MintLink Plus'],
  [/^\/pages\/[^/]+\/edit/, 'Edit Page'],
  [/^\/pages/, 'My Pages'],
];

function appRouteTitle(pathname) {
  const match = APP_ROUTE_TITLES.find(([re]) => re.test(pathname));
  return match ? match[1] : null;
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-2 border-[#32764A] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Catches /@username when Vercel's redirect doesn't fire (local dev / edge cases)
function AtSymbolCatcher() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/@')) {
    const handle = pathname.slice(2).split('/')[0]; // "/@foo/extra" → "foo"
    if (handle) return <Navigate to={`/c/${handle}`} replace />;
  }
  return null;
}

function AppLayout() {
  const location = useLocation();
  const { isStandaloneApp } = usePlatform();
  // Plus branded checkouts hide the global chrome for a full-screen page
  const { hidden: chromeHidden } = useChromeState();
  const isAdminRoute = location.pathname.startsWith('/admin');
  // Full-screen builders (page builder, event builder, storefront builder) drop
  // the web chrome. /storefront belongs here for the same reason as the others
  // and had simply been missed: it is a three-pane editor with its own header
  // and its own tabs, and rendering it under the global navbar and above the
  // app tab bar spent ~120px of a phone screen framing an app the creator is
  // already inside.
  const isEditorRoute = /^\/pages\/[^/]+\/edit/.test(location.pathname)
    || /^\/events\/(new|[^/]+\/edit)$/.test(location.pathname)
    || /^\/quizzes\/(new|[^/]+\/edit)$/.test(location.pathname)
    || /^\/storefront$/.test(location.pathname);
  const isPublicCreatorRoute = location.pathname.startsWith('/c/');
  // The floating widget loads /creator/:username?widget=1 into an iframe about
  // 400px wide. That route is NOT under /c/, so until now it inherited the full
  // marketing chrome — navbar, hamburger, footer — inside the panel. Nobody
  // reaches that widget except from a creator's own site, and every product
  // link already breaks out to mintlink.in, so a nav menu there is a way out of
  // the shop rather than a way into it.
  const isWidgetRoute = new URLSearchParams(location.search).get('widget') === '1';
  // MintLink Lab pages are full-bleed showpieces on a dark ground and bring
  // their own back link — the white Navbar on top of one is a value clash, and
  // the tab bar invites people away from a demo that is three taps long.
  const isLabRoute = /^\/lab(\/|$)/.test(location.pathname);
  // The Tickets landing is a full-bleed cinematic page with its own immersive
  // nav (src/pages/tickets-landing/components/ImmersiveNav) — the fixed white
  // marketing Navbar over its hero is the same value clash Lab routes avoid.
  // It keeps the global <Footer/>.
  const isTicketsRoute = location.pathname === '/tickets';
  // Public event + ticket pages have their own sticky checkout bar — keep the
  // navbar but suppress the standalone bottom tab bar so they don't overlap.
  const isEventPublicRoute = /^\/(e|ticket)\/[^/]+/.test(location.pathname);
  // Same reasoning for quizzes, plus one of its own: /quiz/:token runs a
  // countdown with a sticky header and a fixed Submit button. A tab bar
  // sitting over the Submit button on a timed test is the worst possible
  // place for one.
  const isQuizPublicRoute = /^\/(q|quiz|qb|bundle)\/[^/]+/.test(location.pathname);
  // Product checkout has the same shape: its own fixed mobile Pay bar, at the
  // same z-index as the tab bar, so the two stacked and buried the Pay button.
  // Suppressing the tab bar also stops it inviting people away mid-payment.
  // /m/:slug is the same Checkout page (see the /m and /checkout routes both
  // rendering <Checkout />) — it's the short-link form most product links
  // actually use, so it needs the same exclusion as /checkout/:slug.
  const isCheckoutRoute = /^\/(checkout|m)\/[^/]+$/.test(location.pathname);
  const showAppChrome = isStandaloneApp && !isAdminRoute && !isEditorRoute
    && !isPublicCreatorRoute && !isEventPublicRoute && !isQuizPublicRoute
    && !isCheckoutRoute && !isLabRoute;
  // Pages that have been given their own NativeHeader (via CreatorLayout/
  // BuyerLayout) so far — only these should lose the web Navbar in standalone
  // mode. Every other route keeps today's behavior untouched.
  const NATIVE_HEADER_ROUTES = [
    /^\/dashboard$/,
    /^\/buyer\/dashboard$/,
    /^\/community$/,
    /^\/analytics$/,
    /^\/orders$/,
    /^\/checkout\/[^/]+$/,
    /^\/m\/[^/]+$/,
    /^\/download\/[^/]+$/,
  ];
  const hasNativeHeader = isStandaloneApp && NATIVE_HEADER_ROUTES.some((re) => re.test(location.pathname));
  useEffect(() => {
    applyConsent(location.pathname);
    pageview(location.pathname + location.search);
    // /admin/* titles itself (AdminLayout, from NAV_GROUPS) and public/
    // marketing pages title themselves via <Helmet> — only set the title here
    // for the authenticated app routes that do neither.
    if (!isAdminRoute) {
      const label = appRouteTitle(location.pathname);
      if (label) document.title = `${label} · MintLink`;
    }
  }, [location, isAdminRoute]);
  useReferral(); // detect ?ref_code= on any page
  return (
    <div className="flex flex-col min-h-screen">
      <ScrollToTop />
      {/* FCM hands foreground pushes to the page instead of showing a system
          banner, so without this they land nowhere at all. */}
      <PushForeground />
      {/* 2026-10-12: Android now stays in the Instagram/FB in-app browser (testing
          whether that causes issues); iOS still auto-escapes — gated inside the
          component itself, see OpenInExternalBrowser.jsx. */}
      {!isPublicCreatorRoute && <OpenInExternalBrowser />}
      {!isAdminRoute && !isEditorRoute && !isPublicCreatorRoute && !isLabRoute && !isTicketsRoute && !isWidgetRoute && !hasNativeHeader && !chromeHidden && <Navbar />}
      {!isStandaloneApp && <InstallPWA/>}
      {/* Top: pages under the fixed web Navbar need its notch inset too, but only
          in the installed app — on the web every inset is 0 and pt-content-top
          collapses to exactly the pt-8 these pages have always had. Routes with a
          NativeHeader keep pt-0: that header is sticky, in-flow, and carries its
          own pt-safe-t.
          Bottom: pb-tabbar clears the fixed AppTabBar. Both are single-value
          calc() tokens — see tailwind.config.js for why that matters. */}
      <main className={`flex-1 ${
        isEditorRoute || isPublicCreatorRoute || isLabRoute || isTicketsRoute || isWidgetRoute || hasNativeHeader || chromeHidden
          ? 'pt-0'
          : 'pt-content-top'
      } ${showAppChrome ? 'pb-tabbar' : ''}`}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* 🌐 PUBLIC ROUTES */}
            <Route
              path="/"
              element={
                <PublicRedirect>
                  <HomePage />
                </PublicRedirect>
              }
            />
            {/* Physical Products is hidden from creators (Sep 2026). The pages
                still exist; these redirects keep old links from landing on them.
                /shop/:slug, /orders and /track stay live for existing orders. */}
            <Route path="/physical-products" element={<Navigate to="/" replace />} />
            {/* Buyer order tracking: token in the URL is the auth, no login. */}
            <Route path="/track/:orderToken" element={<OrderTracking />} />
            <Route
              path="/terms"
              element={
                <PublicRedirect>
                  <Terms />
                </PublicRedirect>
              }
            />
            {/* Press kit. Deliberately NOT wrapped in PublicRedirect — a
                journalist arriving while signed in should still see the kit,
                not be bounced to the dashboard. */}
            <Route path="/press" element={<Press />} />
            <Route
              path="/privacy-policy"
              element={
                <PublicRedirect>
                  <PrivacyPolicy />
                </PublicRedirect>
              }
            />
            <Route
              path="/data-deletion"
              element={
                <PublicRedirect>
                  <DataDeletion />
                </PublicRedirect>
              }
            />
            <Route
              path="/how-it-works"
              element={
                <PublicRedirect>
                  <HowItWorks />
                </PublicRedirect>
              }
            />
            <Route
              path="/payments"
              element={
                <PublicRedirect>
                  <Payments />
                </PublicRedirect>
              }
            />
            {/* Guides. Deliberately NOT wrapped in PublicRedirect: these pages
                exist to be found in search, and a signed-in creator following a
                Google result to one of them should read it, not be bounced to
                their dashboard. */}
            <Route path="/guides" element={<GuidesIndex />} />
            <Route path="/guides/:slug" element={<GuidePage />} />
            <Route
              path="/mintlink-plus"
              element={
                <PublicRedirect>
                  <MintlinkPlus />
                </PublicRedirect>
              }
            />
            <Route
              path="/instagram-auto-dm"
              element={
                <PublicRedirect>
                  <InstagramAutoDMLanding />
                </PublicRedirect>
              }
            />
            <Route
              path="/mintlink-forms"
              element={
                <PublicRedirect>
                  <UpgradeToForms />
                </PublicRedirect>
              }
            />
            <Route
              path="/review-my-work"
              element={
                <PublicRedirect>
                  <ReviewMyWorkLanding />
                </PublicRedirect>
              }
            />
            <Route
              path="/freelance-services"
              element={
                <PublicRedirect>
                  <ServicesLanding />
                </PublicRedirect>
              }
            />
            <Route
              path="/tickets"
              element={
                <PublicRedirect>
                  <TicketsLanding />
                </PublicRedirect>
              }
            />
            {/* /bookings is in the sitemap and may already be indexed/bookmarked —
                redirect rather than break it, so existing links and search results
                still land somewhere real while Google recrawls onto the new URL. */}
            {/* Comparison pages. Public and unconditioned like /guides/:slug —
                these exist to be found in search, so a logged-in visitor
                following a link must land on the page, not be bounced to the
                dashboard. A bare /tickets/vs goes back to the product, and an
                unknown slug is redirected inside ComparePage (api/og.js serves
                a real 404 for the same URL). */}
            <Route path="/tickets/vs" element={<Navigate to="/tickets" replace />} />
            <Route path="/tickets/vs/:slug" element={<ComparePage />} />
            <Route path="/bookings" element={<Navigate to="/tickets" replace />} />
            <Route
              path="/pricing"
              element={
                <PublicRedirect>
                  <Payments />
                </PublicRedirect>
              }
            />
            <Route
              path="/faq"
              element={
                <PublicRedirect>
                  <FAQ />
                </PublicRedirect>
              }
            />
            <Route
              path="/contact"
              element={
                <PublicRedirect>
                  <Contact />
                </PublicRedirect>
              }
            />
            <Route
              path="/get-started"
              element={
                <PublicRedirect>
                  <GetStarted />
                </PublicRedirect>
              }
            />
            <Route
              path="/login"
              element={
                <PublicRedirect>
                  <Login />
                </PublicRedirect>
              }
            />

            {/* Public checkout & download */}
            <Route path="/shop/:slug" element={<PhysicalCheckout />} />
            {/* Public appreciation-payment page — no auth, no OTP. */}
            <Route path="/support/:username" element={<SupportPage />} />
            <Route path="/m/:slug" element={<Checkout />} />
            <Route path="/checkout/:slug" element={<Checkout />} />
            <Route path="/review/:slug" element={<ReviewServicePage />} />
            <Route path="/service/:slug" element={<GigPage />} />
            <Route path="/form/:slug" element={<PublicForm />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/failure" element={<PaymentFailure />} />
            <Route path="/download/:token" element={<Download />} />
            <Route path="/rate/:token" element={<RatePurchasePage />} />
            <Route path="/forms/download/:token" element={<FormDownload />} />
            <Route path="/upload/:token" element={<UploadViaLink />} />

            {/* Public events / bookings */}
            <Route path="/e/:slug" element={<PublicEvent />} />
            <Route path="/ticket/:token" element={<EventTicket />} />

            {/* Public quizzes. /q/:slug is the shared link; /quiz/:token is
                the attempt itself — the token IS the credential, like
                /ticket/:token, so there is no login in front of it, and it
                never expires. */}
            <Route path="/q/:slug" element={<PublicQuiz />} />
            <Route path="/quiz/:token" element={<QuizAttempt />} />
            <Route path="/qb/:slug" element={<PublicQuizBundle />} />
            <Route path="/bundle/:token" element={<BundlePass />} />

            {/* Public memberships. The token IS the credential, like
                /ticket/:token, so there is no login in front of it.

                /member/, NOT /m/ — that one is already the product checkout
                short link (see the route above), and it is declared first, so
                a member card under /m/ would silently render Checkout for
                every member who clicked their own link. */}
            <Route path="/join/:username" element={<JoinMembership />} />
            <Route path="/member/:token" element={<MemberCard />} />
            {/* A desk attendant has no account — the join code is the credential. */}
            <Route path="/desk" element={<DeskLink />} />

            {/* Buyer routes */}
            <Route path="/buyer/login" element={<BuyerLogin />} />
            <Route path="/buyer/dashboard" element={<BuyerDashboard />} />
            <Route path="/review-order/:orderId" element={<ReviewOrderBuyerView />} />
            <Route path="/service-order/:orderId" element={<GigOrderBuyerView />} />

            {/* Referral landing — no auth, just track + redirect to signup */}
            <Route path="/ref/:code" element={<ReferralLanding />} />

            {/* Public blog */}
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />

            {/* Public creator pages */}
            <Route path="/creators" element={<CreatorMarketplace />} />
            <Route path="/explore" element={<CreatorMarketplace />} />
            <Route path="/marketplace" element={<ProductMarketplace />} />
            <Route path="/marketplace/:category" element={<ProductMarketplace />} />
            {/* /c/:username — canonical short profile, served via the page builder renderer */}
            {/* /o/:username — the Plus storefront. Separate namespace from
                /c/:username so a creator can run both at once. */}
            <Route path="/o/:username" element={<PublicStorefront />} />
            <Route path="/o/:username/c/:collectionId" element={<CoursePage />} />
            <Route path="/o/:username/c/:collectionId/checkout" element={<CourseCheckout />} />
            <Route path="/o/:username/library" element={<Library />} />
            <Route path="/o/:username/library/:collectionId" element={<LibraryCourse />} />
            <Route path="/o/:username/:pageSlug" element={<PublicStorefront />} />
            <Route path="/c/:username" element={<PublicPage />} />
            {/* /c/:username/:pageSlug — additional creator pages */}
            <Route path="/c/:username/:pageSlug" element={<PublicPage />} />
            {/* /creator/:username — legacy long form / marketplace view, kept for backward compat */}
            <Route path="/creator/:username" element={<PublicCreatorProfile />} />

            {/* 🔐 PROTECTED CREATOR ROUTES */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <CreatorDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
            <Route path="support/new" element={<ProtectedRoute><NewTicket /></ProtectedRoute>} />
            <Route path="support/tickets/:ticketId" element={<PublicRedirect><TicketDetails /></PublicRedirect>} />
            <Route
              path="/payouts"
              element={
                <ProtectedRoute>
                  <Payouts />
                </ProtectedRoute>
              }
            />
            {/* 🧪 MINTLINK LAB — admins only. One guarded parent so a new lab
                page can't be added outside the guard by accident: the lab
                spends the Pexels quota the creator-facing picker shares, and
                loads third-party code onto this origin. */}
            <Route
              path="/lab"
              element={
                <ProtectedRoute requireAdmin>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<LabIndex />} />
              <Route path="receipt" element={<ReceiptLab />} />
              <Route path="buyer-journey" element={<BuyerJourneyLab />} />
              <Route path="palette" element={<PaletteLab />} />
              <Route path="composer" element={<PosterComposerLab />} />
              <Route path="carousel" element={<CarouselComposerLab />} />
              <Route path="components" element={<ComponentFactoryLab />} />
              <Route path="mintlink-deck" element={<MintlinkDeckLab />} />
              <Route path="storefront" element={<StorefrontShowroom />} />
              <Route path="pdf-form-filler" element={<PdfFormFillerLab />} />
              <Route path="site-generator" element={<SiteGeneratorLab />} />
            </Route>

            <Route
              path="/studio/poster"
              element={
                <ProtectedRoute>
                  <PosterStudio />
                </ProtectedRoute>
              }
            />
            {/* /website is the hub — Builder, Templates, Domain and SEO as four
                destinations. /storefront stays the editor, and stays the place
                a creator spends their time; this is what they land on first.
                Ordered before it only for readability. */}
            <Route
              path="/website"
              element={
                <ProtectedRoute>
                  <WebsiteHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/website/templates"
              element={
                <ProtectedRoute>
                  <WebsiteTemplates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/website/seo"
              element={
                <ProtectedRoute>
                  <WebsiteSeo />
                </ProtectedRoute>
              }
            />
            <Route
              path="/storefront"
              element={
                <ProtectedRoute>
                  <StorefrontBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/storefront/leads"
              element={
                <ProtectedRoute>
                  <StorefrontLeads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/storefront/domain"
              element={
                <ProtectedRoute>
                  <StorefrontDomain />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <CreatorProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bank-details"
              element={
                <ProtectedRoute>
                  <BankDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <ProtectedRoute>
                  <Transactions />
                </ProtectedRoute>
              }
            />
            {/* A creator's side of a takedown — linked from the takedown email
                and bell notification. See pages/ListingReports.jsx. */}
            <Route path="/listing-reports" element={<ProtectedRoute><ListingReports /></ProtectedRoute>} />
            <Route path="/listing-reports/:reportId" element={<ProtectedRoute><ListingReports /></ProtectedRoute>} />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-verification"
              element={
                <ProtectedRoute>
                  <BusinessVerification />
                </ProtectedRoute>
              }
            />
            <Route path="/physical-commerce" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/supporters"
              element={
                <ProtectedRoute>
                  <Supporters />
                </ProtectedRoute>
              }
            />
            {/* Events / Bookings */}
            <Route path="/events" element={<ProtectedRoute><EventsList /></ProtectedRoute>} />
            <Route path="/events/new" element={<ProtectedRoute><EventEditor /></ProtectedRoute>} />
            <Route path="/events/:eventId/edit" element={<ProtectedRoute><EventEditor /></ProtectedRoute>} />
            <Route path="/events/:eventId/attendees" element={<ProtectedRoute><EventAttendees /></ProtectedRoute>} />
            <Route path="/events/:eventId/capacity" element={<ProtectedRoute><EventCapacity /></ProtectedRoute>} />
            <Route path="/memberships" element={<ProtectedRoute><MembershipsList /></ProtectedRoute>} />
            <Route path="/memberships/new" element={<ProtectedRoute><MembershipEditor /></ProtectedRoute>} />
            <Route path="/memberships/:planId/edit" element={<ProtectedRoute><MembershipEditor /></ProtectedRoute>} />
            <Route path="/memberships/:planId/members" element={<ProtectedRoute><MembershipMembers /></ProtectedRoute>} />
            <Route path="/memberships/check-in" element={<ProtectedRoute><MembershipCheckIn /></ProtectedRoute>} />
            <Route path="/memberships/retention" element={<ProtectedRoute><MembershipAnalytics /></ProtectedRoute>} />
            <Route path="/memberships/staff" element={<ProtectedRoute><MembershipStaff /></ProtectedRoute>} />
            <Route
              path="/referrals"
              element={
                <ProtectedRoute>
                  <Referrals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reviews"
              element={
                <ProtectedRoute>
                  <CreatorReviews />
                </ProtectedRoute>
              }
            />
            {/* Creator-issued discount codes + per-code sales attribution.
                NB: /referrals is a different feature — recruiting creators. */}
            <Route path="/discount-codes" element={<ProtectedRoute><DiscountCodes /></ProtectedRoute>} />
            <Route path="/connected-apps" element={<ProtectedRoute><ConnectedApps /></ProtectedRoute>} />
            <Route path="/connect/authorize" element={<ConnectAuthorize />} />
            <Route path="/digital-products" element={<ProtectedRoute><DigitalProductsList /></ProtectedRoute>} />
            {/* Community — anonymous, creators-only forum. /join is a real
                route rather than a modal so the gate survives a refresh and
                can be linked to from an announcement. */}
            <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
            <Route path="/community/join" element={<ProtectedRoute><CommunityJoin /></ProtectedRoute>} />
            <Route path="/community/me" element={<ProtectedRoute><CommunityMe /></ProtectedRoute>} />
            <Route path="/community/:postId" element={<ProtectedRoute><CommunityThread /></ProtectedRoute>} />
            {/* Review My Work — creator service catalog + order fulfillment */}
            <Route path="/review-services" element={<ProtectedRoute><ReviewServicesList /></ProtectedRoute>} />
            <Route path="/review-services/new" element={<ProtectedRoute><ReviewServiceEditor /></ProtectedRoute>} />
            <Route path="/review-services/:serviceId/edit" element={<ProtectedRoute><ReviewServiceEditor /></ProtectedRoute>} />
            <Route path="/forms" element={<ProtectedRoute><FormsList /></ProtectedRoute>} />
            <Route path="/forms/new" element={<ProtectedRoute><FormsEditor /></ProtectedRoute>} />
            <Route path="/forms/:formId/edit" element={<ProtectedRoute><FormsEditor /></ProtectedRoute>} />

            {/* MintLink Quiz — teacher side */}
            <Route path="/quizzes" element={<ProtectedRoute><QuizzesList /></ProtectedRoute>} />
            <Route path="/quizzes/new" element={<ProtectedRoute><QuizNew /></ProtectedRoute>} />
            <Route path="/quizzes/:quizId/edit" element={<ProtectedRoute><QuizEditor /></ProtectedRoute>} />
            <Route path="/quizzes/:quizId/results" element={<ProtectedRoute><QuizResults /></ProtectedRoute>} />
            <Route path="/quiz-bundles" element={<ProtectedRoute><QuizBundlesList /></ProtectedRoute>} />
            <Route path="/quiz-bundles/new" element={<ProtectedRoute><QuizBundleEditor /></ProtectedRoute>} />
            <Route path="/quiz-bundles/:bundleId/edit" element={<ProtectedRoute><QuizBundleEditor /></ProtectedRoute>} />
            <Route path="/courses" element={<ProtectedRoute><CoursesList /></ProtectedRoute>} />
            <Route path="/courses/new" element={<ProtectedRoute><CourseEditor /></ProtectedRoute>} />
            <Route path="/courses/:id" element={<ProtectedRoute><CourseEditor /></ProtectedRoute>} />
            <Route path="/review-orders" element={<ProtectedRoute><ReviewOrders /></ProtectedRoute>} />
            <Route path="/review-orders/:orderId" element={<ProtectedRoute><ReviewOrderDetail /></ProtectedRoute>} />
            {/* Services — the freelance marketplace: seller catalog + order fulfilment.
                Distinct from Review My Work above; see constants/gigService.js on why
                the code says "gig" while everything the user reads says "service". */}
            <Route path="/services" element={<ProtectedRoute><GigsList /></ProtectedRoute>} />
            <Route path="/services/new" element={<ProtectedRoute><GigEditor /></ProtectedRoute>} />
            <Route path="/services/:gigId/edit" element={<ProtectedRoute><GigEditor /></ProtectedRoute>} />
            <Route path="/service-orders" element={<ProtectedRoute><GigOrders /></ProtectedRoute>} />
            <Route path="/service-orders/:orderId" element={<ProtectedRoute><GigOrderDetail /></ProtectedRoute>} />
            {/* Instagram Auto-Reply (AutoDM) — OAuth redirects back to /dashboard/instagram */}
            <Route
              path="/dashboard/instagram"
              element={
                <ProtectedRoute>
                  <InstagramAutoDM />
                </ProtectedRoute>
              }
            />
            {/* MintLink Plus */}
            <Route
              path="/plus"
              element={
                <ProtectedRoute>
                  <PlusUpgrade />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plus/success"
              element={
                <ProtectedRoute>
                  <PlusSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plus/billing"
              element={
                <ProtectedRoute>
                  <PlusBilling />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plus/customize"
              element={
                <ProtectedRoute>
                  <CheckoutCustomizer />
                </ProtectedRoute>
              }
            />
            <Route path="/plus/customize-physical" element={<Navigate to="/plus/customize" replace />} />

            {/* Link-in-bio page builder */}
            <Route
              path="/pages"
              element={
                <ProtectedRoute>
                  <MyPages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pages/:pageId/edit"
              element={
                <ProtectedRoute>
                  <PageBuilder />
                </ProtectedRoute>
              }
            />

            {/* 🔐 ADMIN ROUTES */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="creators" element={<AdminCreatorsList />} />
              <Route path="creators/:creatorId" element={<AdminCreatorDetails />} />
              <Route path="assets" element={<AdminAssetsList />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="memberships" element={<AdminMemberships />} />
              <Route path="pdf-forms" element={<AdminPdfForms />} />
              <Route path="quizzes" element={<AdminQuizzes />} />
              <Route path="events/:creatorId/:eventId" element={<AdminEventDetail />} />
              <Route path="events/:creatorId/:eventId/edit" element={<EventEditor />} />
              <Route path="sales" element={<AdminSales />} />
              <Route path="discount-codes" element={<AdminDiscountCodes />} />
              <Route path="review-orders" element={<AdminReviewOrders />} />
              <Route path="upload-scans" element={<AdminUploadScans />} />
              <Route path="review-orders/:orderId" element={<AdminReviewOrderDetail />} />
              <Route path="review-services" element={<AdminReviewServicesList />} />
              <Route path="review-services/:serviceId" element={<AdminReviewServiceDetail />} />
              <Route path="gig-orders" element={<AdminGigOrders />} />
              <Route path="gig-orders/:orderId" element={<AdminGigOrderDetail />} />
              <Route path="gigs" element={<AdminGigsList />} />
              <Route path="gigs/:gigId" element={<AdminGigDetail />} />
              <Route path="payouts" element={<AdminPayouts />} />
              <Route path="tax-invoices" element={<AdminTaxInvoices />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="geography" element={<AdminVisitorGeography />} />
              <Route path="google-dorks" element={<AdminGoogleDorks />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="audit-log" element={<AdminAuditLog />} />
              <Route path="support" element={<AdminSupportConsole />} />
              <Route path="support/tickets/:ticketId" element={<AdminSupportConsole />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="bank-verification" element={<AdminBankVerification />} />
              <Route path="business-verifications" element={<AdminBusinessVerifications />} />
              <Route path="business-verifications/:creatorId" element={<AdminBusinessVerificationDetail />} />
              <Route path="physical-orders" element={<AdminPhysicalOrders />} />
              <Route path="physical-categories" element={<AdminPhysicalCategories />} />
              <Route path="digital-categories" element={<AdminDigitalCategories />} />
              <Route path="blog-management" element={<AdminBlogManagement />} />
              <Route path="api-trail" element={<AdminApiTrail />} />
              <Route path="error-logs" element={<AdminCreatorErrors />} />
              <Route path="email-logs" element={<AdminEmailLogs />} />
              <Route path="upload-logs" element={<AdminUploadLogs />} />
              <Route path="listing-reports" element={<AdminListingReports />} />
              <Route path="review-reports" element={<AdminReviewReports />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="upload-help" element={<AdminCreatorUploadHelp />} />
              <Route path="referrals" element={<AdminReferrals />} />
              <Route path="community" element={<AdminCommunity />} />
              <Route path="planner" element={<AdminPlanner />} />
              <Route path="push" element={<AdminPush />} />
              <Route path="push-deliveries" element={<AdminPushDeliveries />} />
              <Route path="referrals/:creatorId" element={<AdminReferralDetail />} />
              <Route path="supporters" element={<AdminSupporters />} />
              <Route path="supporters/:creatorId" element={<AdminSupporterDetail />} />
              <Route path="announcements" element={<AdminAnnouncements />} />
              <Route path="api-metrics" element={<AdminApiMetrics />} />
              <Route path="pages" element={<AdminPagesList />} />
              <Route path="websites" element={<AdminWebsites />} />
              <Route path="funnel" element={<AdminFunnel />} />
              <Route path="marketplace" element={<AdminMarketplace />} />
              <Route path="plus" element={<AdminPlusSubscribers />} />
              <Route path="instagram" element={<AdminInstagramDM />} />
              <Route path="social-calendar" element={<AdminSocialCalendar />} />
              {/* The single-network scheduler this replaced. */}
              <Route path="instagram-scheduler" element={<Navigate to="/admin/social-calendar" replace />} />
              <Route path="roles" element={<AdminRoles />} />
              <Route path="local-llms" element={<AdminLocalLLMs />} />
              <Route path="agent-metrics" element={<AdminAgentMetrics />} />
            </Route>

            {/* Safety net: catch /@username that React Router can't match directly */}
            <Route path="*" element={<AtSymbolCatcher />} />
          </Routes>
        </Suspense>
      </main>

      {!isAdminRoute && !isEditorRoute && !isPublicCreatorRoute && !isLabRoute && !isWidgetRoute && !isStandaloneApp && !chromeHidden && <Footer />}
      {showAppChrome && <AppTabBar />}
    </div>
  );
}

/**
 * {username}.mintlink.in
 *
 * Checked once, before the router exists, because on a creator subdomain there
 * is no MintLink app to render — no navbar, no tab bar, no marketing home. The
 * whole host is that creator's website, so `/` is their storefront and every
 * other path is a 404 on their site rather than a page of ours.
 *
 * `storefrontHostFor` returns null for anything it is not certain about, so the
 * default is always "render MintLink normally".
 */
function SubdomainSite({ username }) {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<PublicStorefront usernameOverride={username} />} />
          {/* Deep links a creator shared before the subdomain existed still
              work, so /o/name on their own host is their own site. */}
          <Route path="/o/:username" element={<PublicStorefront />} />
          <Route path="/o/:username/c/:collectionId" element={<CoursePage />} />
          <Route path="/o/:username/c/:collectionId/checkout" element={<CourseCheckout />} />
          <Route path="/o/:username/library" element={<Library />} />
          <Route path="/o/:username/library/:collectionId" element={<LibraryCourse />} />
          <Route path="/o/:username/:pageSlug" element={<PublicStorefront />} />
          {/* Courses & Content on the creator's own host: the course page,
              its checkout and the buyer library. Declared before /:pageSlug —
              'c' and 'library' are reserved page slugs for exactly this. */}
          <Route path="/c/:collectionId" element={<CoursePage usernameOverride={username} />} />
          <Route path="/c/:collectionId/checkout" element={<CourseCheckout usernameOverride={username} />} />
          <Route path="/library" element={<Library usernameOverride={username} />} />
          <Route path="/library/:collectionId" element={<LibraryCourse usernameOverride={username} />} />
          {/* Their other pages — /about, /contact — live at the top level of
              their own host, which is what makes it read as a real website
              rather than a page hosted on somebody else's. */}
          <Route path="/:pageSlug" element={<PublicStorefront usernameOverride={username} subdomain />} />
          <Route path="*" element={<PublicStorefront usernameOverride={username} subdomain />} />
        </Routes>
      </Suspense>
      <ConsentBanner />
      <Toaster position="top-center" toastOptions={{ style: { zIndex: 100 } }} />
    </BrowserRouter>
  );
}

/**
 * A creator's own domain — example.com pointed at us.
 *
 * The one thing that makes this different from a subdomain: the hostname
 * carries no username, so the site cannot be rendered until the server says
 * whose it is. That is one request before first paint, and it is why this
 * exists as its own component rather than a branch inside SubdomainSite.
 *
 * The failure state matters as much as the success one. A domain whose DNS
 * arrived here before the creator finished connecting it in MintLink — or
 * after they removed it — must say so plainly, because the person looking at
 * it is either the creator mid-setup or a customer of theirs. Rendering
 * MintLink's marketing homepage on somebody's own domain would be the worst
 * of the available answers.
 */
function CustomDomainSite({ hostname }) {
  const [state, setState] = useState({ loading: true, username: null });

  useEffect(() => {
    let cancelled = false;
    // Plain fetch rather than apiService: this runs at the app's entry point,
    // and importing the axios service here would pull it (and its auth
    // interceptors) into the root bundle every visitor downloads — to make one
    // unauthenticated GET that only custom-domain visitors ever make.
    fetch(`${API_BASE_URL}/api/domains/resolve/${encodeURIComponent(hostname)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => { if (!cancelled) setState({ loading: false, username: res?.username || null }); })
      .catch(() => { if (!cancelled) setState({ loading: false, username: null }); });
    return () => { cancelled = true; };
  }, [hostname]);

  if (state.loading) return <PageLoader />;

  if (!state.username) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#fff', padding: 24, textAlign: 'center', fontFamily: 'ui-sans-serif, system-ui' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>
            This domain isn’t connected yet
          </h1>
          <p style={{ color: '#6B7280', marginTop: 8, fontSize: 14.5, maxWidth: 420 }}>
            {hostname} points here, but no MintLink website is claiming it. If this is
            your domain, connect it from your storefront settings.
          </p>
          <a href={`https://${ROOT_DOMAIN}/storefront`}
            style={{ display: 'inline-block', marginTop: 18, color: '#059669', fontWeight: 600, fontSize: 14 }}>
            Open MintLink →
          </a>
        </div>
      </div>
    );
  }

  return <SubdomainSite username={state.username} />;
}

function App() {
  const storefrontHost = currentStorefrontHost();
  if (storefrontHost) return <SubdomainSite username={storefrontHost} />;

  // Checked after the subdomain, because *.mintlink.in needs no lookup and
  // must never pay for one.
  const customDomain = currentCustomDomain();
  if (customDomain) return <CustomDomainSite hostname={customDomain} />;

  return (
    <BrowserRouter>
      <PlatformProvider>
        <ChromeProvider>
          <AppLayout />
          {/* Analytics consent (DPDP). Nothing in utils/consent.js loads until
              the visitor answers here. */}
          <ConsentBanner />
          {/* Mounted once, at the root: every toast.success/error call in the
              app — 9 call sites across reviews, admin, page builder and this
              feature — renders through this single portal. Without it those
              calls resolve silently; nothing was broken in each caller, there
              was simply nowhere for the result to appear. z-[100] clears the
              poster generator's full-screen z-[60] overlay. */}
          <Toaster
            position="top-center"
            toastOptions={{ style: { zIndex: 100 } }}
            containerStyle={{ zIndex: 100 }}
          />
          {/* Vercel Web Analytics. Cookieless and aggregate — it counts page
              views and does not identify anyone, which is why it sits here
              rather than behind a consent gate.

              Alongside GA (utils/ga.js) rather than replacing it: GA carries
              the events the dashboard reports on, this answers "how much
              traffic, from where" without sampling. Route changes are picked
              up automatically — it patches the history API, which is what an
              SPA needs. */}
          <VercelAnalytics />
        </ChromeProvider>
      </PlatformProvider>
    </BrowserRouter>
  );
}

export default App;
